import { BossKind, EnemyType } from '../constants';

export const enum StageCueKind { SpawnWave = 0, Gate = 1, MusicLayer = 2, ScrollChange = 3, MidBoss = 4, FinalBoss = 5, Quiet = 6 }

export interface StageCue {
  readonly tick: number;
  readonly kind: StageCueKind;
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

export interface StageSpec {
  readonly id: number;
  readonly name: string;
  readonly subtitle: string;
  readonly durationTicks: number;
  readonly baseScroll: number;
  readonly paletteRow: number;
  readonly tileName: string;
  readonly cues: readonly StageCue[];
}

/** 正本データは旧経路互換の原寸tick。R2の時間圧縮はStageDirector境界でだけ行う。 */
export const STAGE_TIMELINE_SCALE = 1;
const at = (tick: number): number => Math.round(tick * STAGE_TIMELINE_SCALE);
const wave = (tick: number, enemy: EnemyType, count: number, pattern: number): StageCue => Object.freeze({ tick: at(tick), kind: StageCueKind.SpawnWave, a: enemy, b: count, c: pattern });
const gate = (tick: number): StageCue => Object.freeze({ tick: at(tick), kind: StageCueKind.Gate, a: 0, b: 0, c: 0 });
const music = (tick: number, layer: number): StageCue => Object.freeze({ tick: at(tick), kind: StageCueKind.MusicLayer, a: layer, b: 0, c: 0 });
const scroll = (tick: number, value100: number): StageCue => Object.freeze({ tick: at(tick), kind: StageCueKind.ScrollChange, a: value100, b: 0, c: 0 });
const boss = (tick: number, kind: BossKind, mid: boolean): StageCue => Object.freeze({ tick: at(tick), kind: mid ? StageCueKind.MidBoss : StageCueKind.FinalBoss, a: kind, b: 0, c: 0 });
const quiet = (tick: number): StageCue => Object.freeze({ tick: at(tick), kind: StageCueKind.Quiet, a: 0, b: 0, c: 0 });

export const STAGES: readonly StageSpec[] = Object.freeze([
  Object.freeze({
    id: 0, name: 'GLASS CURRENT', subtitle: 'REFRACTED RECORD RIVER', durationTicks: at(7200), baseScroll: 0.72, paletteRow: 0, tileName: 'tile_glass',
    cues: Object.freeze([
      music(1, 0), wave(180, EnemyType.Threadling, 6, 0), wave(600, EnemyType.PlateFin, 4, 1), wave(1050, EnemyType.Indexer, 3, 2),
      music(1500, 1), wave(1700, EnemyType.Threadling, 10, 3), wave(2300, EnemyType.ShadePolyp, 5, 4), gate(3000),
      scroll(3300, 95), wave(3500, EnemyType.MirrorWisp, 5, 5), wave(4300, EnemyType.LockBeetle, 4, 2), music(4800, 2),
      wave(5100, EnemyType.ArchiveRam, 3, 6), wave(5800, EnemyType.ChoirEngine, 2, 7), gate(6600), quiet(6700),
      boss(6900, BossKind.CatalogueMouth, false),
    ]),
  }),
  Object.freeze({
    id: 1, name: 'INDEX GARDEN', subtitle: 'MECHANICAL CLASSIFICATION BLOOM', durationTicks: at(7600), baseScroll: 0.62, paletteRow: 1, tileName: 'tile_garden',
    cues: Object.freeze([
      music(1, 0), wave(180, EnemyType.ShadePolyp, 6, 4), wave(700, EnemyType.PlateFin, 6, 1), wave(1250, EnemyType.LockBeetle, 4, 5),
      music(1700, 1), wave(2000, EnemyType.Indexer, 5, 2), wave(2600, EnemyType.MirrorWisp, 6, 3), gate(3150),
      scroll(3400, 78), wave(3600, EnemyType.Threadling, 12, 0), wave(4300, EnemyType.PrismEater, 2, 7), boss(5200, BossKind.CatalogueMouth, true),
      music(5900, 2), wave(6400, EnemyType.ArchiveRam, 4, 6), gate(7100), quiet(7200),
      boss(7350, BossKind.NullScribe, false),
    ]),
  }),
  Object.freeze({
    id: 2, name: 'INVERSE CITY', subtitle: 'RECIPROCAL TRANSIT MEMORY', durationTicks: at(8000), baseScroll: 0.9, paletteRow: 2, tileName: 'tile_city',
    cues: Object.freeze([
      music(1, 0), wave(120, EnemyType.FoldRay, 3, 8), wave(620, EnemyType.Threadling, 10, 9), scroll(1100, 130),
      wave(1450, EnemyType.Indexer, 6, 2), music(1900, 1), gate(2250), wave(2500, EnemyType.PlateFin, 8, 10),
      scroll(3100, 65), wave(3350, EnemyType.ChoirEngine, 3, 7), wave(4000, EnemyType.PrismEater, 3, 5), gate(4550),
      music(4900, 2), scroll(5150, 145), wave(5300, EnemyType.ArchiveRam, 5, 6), wave(6100, EnemyType.FoldRay, 5, 8),
      wave(6800, EnemyType.LockBeetle, 8, 11), gate(7400), quiet(7500),
      boss(7700, BossKind.NullScribe, false),
    ]),
  }),
  Object.freeze({
    id: 3, name: 'BLACK REGISTER', subtitle: 'CORRUPTED CENTRAL RECORD', durationTicks: at(9400), baseScroll: 0.78, paletteRow: 3, tileName: 'tile_register',
    cues: Object.freeze([
      music(1, 0), wave(120, EnemyType.Threadling, 12, 3), wave(500, EnemyType.PlateFin, 8, 1), wave(900, EnemyType.MirrorWisp, 8, 5),
      music(1400, 1), wave(1550, EnemyType.Indexer, 7, 2), wave(2100, EnemyType.LockBeetle, 6, 11), gate(2700),
      wave(3000, EnemyType.ChoirEngine, 4, 7), wave(3600, EnemyType.PrismEater, 4, 6), wave(4200, EnemyType.ArchiveRam, 6, 10),
      boss(5000, BossKind.NullScribe, true), music(5900, 2), wave(6100, EnemyType.FoldRay, 6, 8), wave(6750, EnemyType.ChoirEngine, 4, 9),
      gate(7300), quiet(7400), music(7800, 3), boss(8050, BossKind.MnemosyneArray, false),
    ]),
  }),
]);

export function getStage(id: number): StageSpec {
  return STAGES[id] ?? STAGES[0]!;
}
